import { useEffect, useRef } from 'react';

export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const getFocusable = (container) => Array.from(container?.querySelectorAll(FOCUSABLE_SELECTOR) || [])
  .filter((element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true');

/**
 * Shared modal contract for dialogs and sheets: initial focus, Tab containment,
 * Escape dismissal, scroll locking, and focus restoration.
 */
export const useModalFocus = ({ open, containerRef, onDismiss, initialFocusRef }) => {
  const previousFocusRef = useRef(null);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    previousFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusInitial = () => {
      const container = containerRef.current;
      const target = initialFocusRef?.current
        || container?.querySelector('[data-autofocus]')
        || getFocusable(container)[0]
        || container;
      target?.focus?.({ preventScroll: true });
    };
    const frame = window.requestAnimationFrame(focusInitial);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onDismissRef.current?.(event, 'escapeKeyDown');
        return;
      }

      if (event.key !== 'Tab') return;
      const container = containerRef.current;
      const focusable = getFocusable(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !container?.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      const previous = previousFocusRef.current;
      if (previous?.isConnected) {
        window.requestAnimationFrame(() => previous.focus?.({ preventScroll: true }));
      }
    };
  }, [containerRef, initialFocusRef, open]);
};

export default useModalFocus;
